import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import linq from 'linq';
import { Paper, Fab } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { SheetKinds } from '../redux/sheetDbSlice'
import { StoryCard, MapCard, GridCard, CardDb, CardDbHashType, generateCardId, isStoryCard, isMapCard, isGridCard, isMemoCard, isDrawCard, getCardBase, addCard, updateCard, deleteCard } from '../redux/cardDbSlice';
import { CardAttributeDbHashType, addCardAttribute } from '../redux/cardAttributeDbSlice';
import { CardShapeDbHashType, addCardShape } from '../redux/cardShapeDbSlice';
import { MemoCardContentDbHashType, addMemoCardContent } from '../redux/memoCardContentDbSlice';
import { UiCardItemDbHashType, generateUiCardItemId, addUiCardItem } from '../redux/uiCardItemDbSlice';
import { DrawCardContentDbHashType, addDrawCardContent } from '../redux/drawCardContentDbSlice';
import { ArrowDb, addArrow } from '../redux/arrowDbSlice';
import useWindowSize from '@rehooks/window-size'
import useCurrentDb from '../hooks/useCurrentDb'
import useAllCardDbsInSheet from '../hooks/useAllCardDbsInSheet';
import useArrowDbsInSheet from '../hooks/useArrowDbsInSheet'
import useCurrentSheetDb from '../hooks/useCurrentSheetDb'
import useCurrentUserId from '../hooks/useCurrentUserId';
import useUserCondition from '../hooks/useUserCondition';
import { CoordinateType } from '../general';
import { initial, getCardSize } from '../card_components';
import { WorkAreaStateType, CardMenuAction, ArrowMenuAction, HandlingStates, HandlingStateSetters, CardAddActionState } from './BookEditor';
import StorySheet from './StorySheet'
import MapSheet from './MapSheet'
import GridSheet from './GridSheet'
import WorkAreaHeader from './WorkAreaHeader';
import WorkAreaBottomBar from './WorkAreaBottomBar'
import CardMenu from './CardMenu';
import BlankMenu from './BlankMenu';
import ArrowMenu from './ArrowMenu';
import AddCardDialog from './AddCardDialog'
import EditDialog from './EditDialog'
import DeleteCardDialog from './DeleteCardDialog'
import AddCardLinkDialog from './AddCardLinkDialog'
import JumpLinkDialog from './JumpLinkDialog'
import EditArrowDialog from './EditArrowDialog'
import AddArrowLinkDialog from './AddArrowLinkDialog'
import JumpArrowLinkDialog from './JumpArrowLinkDialog'
import DeleteArrowDialog from './DeleteArrowDialog';
import DownloadScreenshotDialog from './DownloadScreenshotDialog';
import CardResizeModal from './CardResizeModal';

const WorkAreaPaper = styled(Paper)`
  position: relative;
  overflow: auto;
  padding: 0;
  z-index: 2;
  box-sizing: border-box;
  border: 1px #eeeeee;
  border-style: none none solid none;
`
const WorkAreaBody = styled.div`
  position: absolute;
  box-sizing: border-box;
  padding: 10px;
  transform-origin: left top;
`
const AddFab = styled(Fab)`
  position: fixed;
  bottom: 96px;
  color: white;
  z-index: 4;
  background-color: #1e88e5;
  &:hover {
    background-color: #1976d2;
  }
`

type WorkAreaProps = {
  infoClicked: (workArea: WorkAreaStateType) => void;
  nodeClicked: (workArea: WorkAreaStateType, id?: string) => void;
  handlingStates: HandlingStates;
  handlingStateSetters: HandlingStateSetters;
  addFabOffset: number;
  cardAddAction: CardAddActionState;
  setCardAddAction: (state: CardAddActionState) => void;
}
const WorkArea: React.FC<WorkAreaProps> = ({ infoClicked, nodeClicked, handlingStates, handlingStateSetters, addFabOffset, cardAddAction, setCardAddAction }) => {
  const dispatch = useDispatch()
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2)
  const workAreaBodyId = 'work-area-body';
  const cardAttributes = useSelector((state: RootState) => state.cardAttributeDb).cardAttributes;
  const cardShapes = useSelector((state: RootState) => state.cardShapeDb).cardShapes;
  const memoCardContents = useSelector((state: RootState) => state.memoCardContentDb).memoCardContents;
  const uiCardItems = useSelector((state: RootState) => state.uiCardItemDb).uiCardItems;
  const drawCardContents = useSelector((state: RootState) => state.drawCardContentDb).drawCardContents;

  const { showMenu, blankMenuData, arrowMenuData, clickRect, storyIndex, mode, connectId, selectingGrid, selectedId, addCardPosition } = handlingStates;
  const { setShowMenu, setBlankMenuData, setArrowMenuData, setClickRect, setStoryIndex, setConnectId, setSelectingGrid, setSelectedId, setAddCardPosition } = handlingStateSetters;

  type CardActionState = { show: boolean, id?: string };
  const [editAction, setEditAction] = useState<CardActionState>({ show: false });
  const [addCardLinkAction, setAddCardLinkAction] = useState<CardActionState>({ show: false });
  const [jumpLinkAction, setJumpLinkAction] = useState<CardActionState>({ show: false });
  type DeleteCardAction = { show: boolean, id?: string, sheetId?: string };
  const [deleteCardAction, setDeleteCardAction] = useState<DeleteCardAction>({ show: false });
  type CardResizeActionState = CardActionState & { [P in keyof CoordinateType]?: CoordinateType[P] };
  const [cardResizeAction, setCardResizeAction] = useState<CardResizeActionState>({ show: false });
  type ScreenshotActionState = { show: boolean };
  const [screenshotAction, setScreenshotAction] = useState<ScreenshotActionState>({ show: false });
  const screenshotRef = useRef(null);

  type ArrowActionState = { show: boolean, id?: string };
  const [editArrowAction, setEditArrowAction] = useState<ArrowActionState>({ show: false });
  const [addArrowLinkAction, setAddArrowLinkAction] = useState<ArrowActionState>({ show: false });
  const [jumpArrowLinkAction, setJumpArrowLinkAction] = useState<ArrowActionState>({ show: false });
  const [deleteArrowAction, setDeleteArrowAction] = useState<ArrowActionState>({ show: false });

  const [scale, setScale] = useState<number | number[]>(100);

  const [cardClipboards, setCardClipboards] = useState<CardDbHashType>();
  type AttributesClipboardsHashType = { [key in string]: CardAttributeDbHashType }
  const [attributesClipboards, setAttributesClipboards] = useState<AttributesClipboardsHashType>({});
  type ShapesClipboardsHashType = { [key in string]: CardShapeDbHashType }
  const [shapesClipboards, setShapesClipboards] = useState<ShapesClipboardsHashType>({});
  type MemoContentClipboardsHashType = { [key in string]: MemoCardContentDbHashType }
  const [memoContentClipboards, setMemoContentClipboards] = useState<MemoContentClipboardsHashType>({});
  type UiItemsClipboardsHashType = { [key in string]: UiCardItemDbHashType }
  const [uiItemsClipboards, setUiItemsClipboards] = useState<UiItemsClipboardsHashType>({});
  type DrawContentClipboardsHashType = { [key in string]: DrawCardContentDbHashType }
  const [drawContentClipboards, setDrawContentClipboards] = useState<DrawContentClipboardsHashType>({});
  type NewClipboardStateType = CardAttributeDbHashType | CardShapeDbHashType | MemoCardContentDbHashType | UiCardItemDbHashType | DrawCardContentDbHashType;

  const [sheetId] = useCurrentDb();
  const [ids, cards] = useAllCardDbsInSheet(sheetId);
  const [arrowIds, arrows] = useArrowDbsInSheet(sheetId);
  const currentSheet = useCurrentSheetDb();
  const userId = useCurrentUserId();
  const [, userCondition] = useUserCondition(userId);

  useEffect(() => {
    if (!userCondition) return;
    if (sheetId) {
      setScale(userCondition.zoomRates[sheetId] ? userCondition.zoomRates[sheetId] : 100);
    } else setScale(100);
  }, [userCondition, sheetId])

  const isCorrectType = <T extends NewClipboardStateType>(a: NewClipboardStateType, b: T): a is T => {
    return true;
  }
  useEffect(() => {
    setAttributesClipboards({});
    setShapesClipboards({});
    setMemoContentClipboards({});
    setUiItemsClipboards({});
    setDrawContentClipboards({});
    if (!cardClipboards) return;

    const cardClipboardIds = Object.keys(cardClipboards);
    const newAttributesClipboard: AttributesClipboardsHashType = {};
    const newShapesClipboard: ShapesClipboardsHashType = {};
    const newMemoContentClipboard: MemoContentClipboardsHashType = {};
    const newUiItemsClipboard: UiItemsClipboardsHashType = {};
    const newDrawContentClipboard: DrawContentClipboardsHashType = {};

    const getNewState = (targetId: string, obj: NewClipboardStateType) => {
      const newState: NewClipboardStateType = {};
      const ids = Object.keys(obj).filter(id => obj[id].cardId === targetId);
      ids.forEach(id => newState[id] = obj[id]);
      return newState;
    }

    cardClipboardIds.forEach(id => {
      const target = cardClipboards[id];
      if (target.kind === 'common') {
        const targetAttributesClipboard = getNewState(id, cardAttributes);
        if (isCorrectType(targetAttributesClipboard, cardAttributes)) newAttributesClipboard[id] = targetAttributesClipboard;
        setAttributesClipboards(newAttributesClipboard);
        const targetShapesClipboard = getNewState(id, cardShapes)
        if (isCorrectType(targetShapesClipboard, cardShapes)) newShapesClipboard[id] = targetShapesClipboard;
        setShapesClipboards(newShapesClipboard);
      } else if (target.kind === 'memo') {
        const targetMemoContentClipboard = getNewState(id, memoCardContents);
        if (isCorrectType(targetMemoContentClipboard, memoCardContents)) newMemoContentClipboard[id] = targetMemoContentClipboard;
        setMemoContentClipboards(newMemoContentClipboard);
      } else if (target.kind === 'ui') {
        const targetUiItemsClipboard = getNewState(id, uiCardItems);
        if (isCorrectType(targetUiItemsClipboard, uiCardItems)) newUiItemsClipboard[id] = targetUiItemsClipboard;
        setUiItemsClipboards(newUiItemsClipboard);
      } else if (target.kind === 'draw') {
        const targetDrawContentClipboard = getNewState(id, drawCardContents);
        if (isCorrectType(targetDrawContentClipboard, drawCardContents)) newDrawContentClipboard[id] = targetDrawContentClipboard;
        setDrawContentClipboards(newDrawContentClipboard);
      }
    })
  }, [cardClipboards])

  useEffect(() => {
    if (!editArrowAction.show && !addArrowLinkAction.show && !jumpArrowLinkAction.show && !cardAddAction.show && !deleteArrowAction.show) setSelectedId(undefined);
  }, [editArrowAction, addArrowLinkAction, jumpArrowLinkAction, cardAddAction, deleteArrowAction])

  if (!currentSheet) return <></>;
  let storyLineLength: number | undefined = undefined;
  if (currentSheet.sheetKind === 'story' && cards.length >= 1) storyLineLength = cards.filter(card => isStoryCard(card) && !card.parentId).length;
  else if (currentSheet.sheetKind === 'story' && cards.length === 0) storyLineLength = 0;

  const getClickRect = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setBlankMenuData(undefined);

    const target = ev.target;
    const workAreaBody = document.getElementById(workAreaBodyId);
    if (target === workAreaBody) {
      setShowMenu(undefined);
      setConnectId(undefined);
    }

    const clientRect = ev.currentTarget.getBoundingClientRect();
    const targetRect = { x: ev.pageX - clientRect.x, y: ev.pageY - clientRect.y };
    setClickRect(targetRect);
  }
  const getWorkAreaBodySize = (): { width: number, height: number } => {
    const scaleCoefficient = typeof scale === 'number' ? scale / 100 : 1;
    const workAreaPaper = document.getElementById('work-area-paper');
    const clientWidth = workAreaPaper ? workAreaPaper.clientWidth / scaleCoefficient : undefined;
    const clientHeight = workAreaPaper ? workAreaPaper.clientHeight - 80 / scaleCoefficient : undefined;

    const cardRights: number[] = [];
    const cardBottoms: number[] = [];
    cards.forEach(card => {
      if (isStoryCard(card)) return;
      const cardSize = getCardSize(card);
      cardRights.push(card.x + cardSize.width);
      cardBottoms.push(card.y + cardSize.height);
    })
    cardRights.sort((a, b) => b - a);
    cardBottoms.sort((a, b) => b - a);

    const width = (clientWidth && clientWidth > cardRights[0]) ? clientWidth : cardRights[0];
    const height = (clientHeight && clientHeight > cardBottoms[0]) ? clientHeight : cardBottoms[0];

    return { width, height };
  }
  const getScale = () => {
    return(typeof scale === 'number' ? scale/100 : 1)
  }
  const cardCutIn = (arrowId: string | undefined) => {
    setCardAddAction({ show: true, parent: currentSheet.sheetKind, cutInMode: true, arrowId: arrowId });
  }
  const toggleBlankMenu = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    ev.preventDefault();
    setBlankMenuData(undefined);
    setAddCardPosition(undefined);

    const isMagicaCard = (element: HTMLElement): boolean => {
      const elementClass = element.className;
      if (!element) return false;
      if (typeof elementClass === 'string' && elementClass.includes('magica-card')) return true;
      else if (element.parentElement) return isMagicaCard(element.parentElement);
      else return false;
    }
    const target = ev.target as HTMLElement;
    if (isMagicaCard(target)) return;

    // check clicked grid is blank or not
    const clientRect = ev.currentTarget.getBoundingClientRect();
    const targetRect = { x: ev.pageX - clientRect.x, y: ev.pageY - clientRect.y };
    const scaleCoefficient = typeof scale === 'number' ? scale / 100 : 1;
    const colomnWidth = 220;
    const rowHeight = 144;
    const clickedColomn = Math.floor(targetRect.x / scaleCoefficient / colomnWidth);
    const clickedRow = Math.floor(targetRect.y / scaleCoefficient / rowHeight);
    let blankFlag = true;
    if (currentSheet.sheetKind === 'grid') {
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        if (!isGridCard(card)) break;
        if (clickedColomn === card.column && clickedRow === card.row) {
          blankFlag = false;
          break;
        }
      }
    }
    if (!blankFlag) return;

    const copying = cardClipboards ? Object.keys(cardClipboards).length : 0;
    setBlankMenuData({ x: ev.clientX - 2, y: ev.clientY - 4, copying: copying });
    setAddCardPosition(targetRect);
  }
  const toggleArrowMenuData = (id?: string, arrow?: ArrowDb, ev?: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!id || !arrow || !ev) {
      setArrowMenuData(undefined);
      setSelectedId(undefined);
      return;
    }
    ev.preventDefault();
    if (id === arrowMenuData?.id) {
      setArrowMenuData(undefined);
      setSelectedId(undefined);
    } else {
      setArrowMenuData({ id, arrow, x: ev.clientX - 2, y: ev.clientY - 4 });
      setSelectedId(id);
    }
  }
  const pasteCard = () => {
    if (!currentSheet || !cardClipboards || !addCardPosition) return;
    const cardClipboardIds = Object.keys(cardClipboards);

    const scaleCoefficient = typeof scale === 'number' ? scale / 100 : 1;
    const colomnWidth = 220;
    const rowHeight = 144;
    const clickedColomn = Math.floor(addCardPosition.x / scaleCoefficient / colomnWidth);
    const clickedRow = Math.floor(addCardPosition.y / scaleCoefficient / rowHeight);

    let blankFlag = true
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (!isGridCard(card)) break;
      if (clickedColomn === card.column && clickedRow === card.row) {
        blankFlag = false;
        break;
      }
    }
    if (!blankFlag) return;

    if (currentSheet.sheetKind !== 'story' && cardClipboardIds.length >= 2) return;

    const rootCardId = cardClipboardIds.length >= 2 ? cardClipboardIds.filter(id => {
      const card = cardClipboards[id];
      return isStoryCard(card) && !card.parentId;
    })[0] : undefined;
    const newRootCardId = generateCardId();

    cardClipboardIds.forEach(targetId => {
      const target = cardClipboards[targetId];
      const newCardId = targetId === rootCardId ? newRootCardId : generateCardId();
      const [cardBase, options] = getCardBase(target);
      const isResizableCard = isMemoCard(target) || isDrawCard(target);
      const currentSize = (isResizableCard && currentSheet.sheetKind !== 'map') ? undefined : {
        currentWidth: options.currentWidth,
        currentHeight: options.currentHeight
      };

      if (currentSheet.sheetKind === 'story') {
        if (storyIndex === undefined) {
          dispatch(addCard({
            ...cardBase,
            id: newCardId,
            sheetId: currentSheet.id,
            sheetKind: 'story',
            groupParent: undefined,
            order: ids.length,
            headerText: '',
            parentId: (isStoryCard(target) && targetId !== rootCardId && cardClipboardIds.length >= 2) ? newRootCardId : undefined,
            ...currentSize
          }))
        } else {
          let targetIndex = storyIndex + 1;
          for (let i = 0; i < ids.length; i++) {
            const card = cards[i];
            if (!isStoryCard(card)) return;
            if (card.order === targetIndex) {
              dispatch(updateCard({id: ids[i], order: targetIndex + 1}));
              targetIndex++;
            }
          }
          dispatch(addCard({
            ...cardBase,
            id: newCardId,
            sheetId: currentSheet.id,
            sheetKind: 'story',
            groupParent: undefined,
            order: storyIndex + 1,
            headerText: '',
            parentId: (isStoryCard(target) && targetId !== rootCardId && cardClipboardIds.length >= 2) ? newRootCardId : undefined,
            ...currentSize
          }))
        }
      } else if (currentSheet.sheetKind === 'map') {
        dispatch(addCard({
          ...cardBase,
          id: newCardId,
          sheetId: currentSheet.id,
          sheetKind: 'map',
          groupParent: undefined,
          x: addCardPosition.x,
          y: addCardPosition.y,
          z: getForegroundZ(cardBase.isGroup, undefined),
          ...currentSize
        }))
      } else if (currentSheet.sheetKind === 'grid') {
        dispatch(addCard({
          ...cardBase,
          id: newCardId,
          sheetId: currentSheet.id,
          sheetKind: 'grid',
          groupParent: undefined,
          column: clickedColomn,
          row: clickedRow,
          x: clickedColomn * colomnWidth,
          y: clickedRow * rowHeight,
          ...currentSize
        }))
      }

      if (cardBase.kind === 'common' && attributesClipboards) {
        const targetAttributes = attributesClipboards[targetId];
        const attributeIds = Object.keys(targetAttributes);
        attributeIds.forEach(id => dispatch(addCardAttribute({...targetAttributes[id], cardId: newCardId})));
      }
      if (cardBase.kind === 'common' && shapesClipboards) {
        const targetShapes = shapesClipboards[targetId];
        const shapeIds = Object.keys(targetShapes);
        shapeIds.forEach(id => dispatch(addCardShape({...targetShapes[id], cardId: newCardId})));
      }
      if (cardBase.kind === 'memo' && memoContentClipboards) {
        const targetMemoContents = memoContentClipboards[targetId];
        const memoContentIds = Object.keys(targetMemoContents);
        memoContentIds.forEach(id => dispatch(addMemoCardContent({...targetMemoContents[id], cardId: newCardId})));
      }
      if (cardBase.kind === 'ui' && uiItemsClipboards) {
        const targetUiItems = uiItemsClipboards[targetId];
        const uiItemIds = Object.keys(targetUiItems);
        const rootUiItemIds = uiItemIds.filter(id => targetUiItems[id].parentId === targetId);
        rootUiItemIds.forEach(id => {
          const newUiItemId = generateUiCardItemId();
          dispatch(addUiCardItem({...targetUiItems[id], id: newUiItemId, cardId: newCardId, parentId: newCardId}));
          const childrenIds = uiItemIds.filter(childId => targetUiItems[childId].parentId === id);
          childrenIds.forEach(childId => dispatch(addUiCardItem({...targetUiItems[childId], cardId: newCardId, parentId: newUiItemId})))
        })
      }
      if (cardBase.kind === 'draw' && drawContentClipboards) {
        const targetDrawContents = drawContentClipboards[targetId];
        const drawContentIds = Object.keys(targetDrawContents);
        drawContentIds.forEach(id => dispatch(addDrawCardContent({...targetDrawContents[id], cardId: newCardId})));
      }
    })

    setAddCardPosition(undefined);
  }
  const addChildCard = (parentId: string, selectingCardValue: CardDb) => {
    if (currentSheet.sheetKind !== 'story') return;
    setCardAddAction({ show: true, parent: parentId, selectingCardValue });
  }
  const putDownForwardCard = (isGroup: boolean, groupParent: string | undefined, z: number) => {
    linq.from(ids)
      .select((id, ix) => { return { id: id, value: cards[ix] }})
      .where(data => isMapCard(data.value) &&
        data.value.isGroup === isGroup &&
        data.value.groupParent === groupParent &&
        data.value.z > z
      )
      .forEach(target => {
        if (!isMapCard(target.value)) return;
        dispatch(updateCard({ id: target.id, z: target.value.z - 1 }));
      });
  }
  const putUpForwardCard = (isGroup: boolean, groupParent: string | undefined, z: number) => {
    linq.from(ids)
      .select((id, ix) => { return { id: id, value: cards[ix] }})
      .where(data => isMapCard(data.value) &&
        data.value.isGroup === isGroup &&
        data.value.groupParent === groupParent &&
        data.value.z >= z
      )
      .forEach(target => {
        if (!isMapCard(target.value)) return;
        dispatch(updateCard({ id: target.id, z: target.value.z + 1 }));
      });
  }
  const getForegroundZ = (isGroup: boolean, groupParent: string | undefined): number => {
    return linq.from(cards)
      .where(value => value.isGroup === isGroup && value.groupParent === groupParent)
      .toArray()
      .length;
  }
  const operateGroupChildren = (parentId: string, parent: MapCard, method: 'takeoff' | 'delete', foregroundZ?: number) => {
    const getNestedGroupIds = (id: string): string[] => {
      const res = [id];
      const childGroups = linq.from(ids).where((targetId, targetIx) => cards[targetIx].groupParent === id && cards[targetIx].isGroup).toArray();
      if (childGroups.length >= 1) childGroups.forEach(groupId => res.push(...getNestedGroupIds(groupId)))
      if (id === parentId) console.log(res)
      return res;
    }
    const getWhereCondition = (value: CardDb): boolean => {
      if (!value.groupParent) return false;
      if (method === 'takeoff') return value.groupParent === parentId;
      else return getNestedGroupIds(parentId).includes(value.groupParent);
    }
    const children = linq.from(ids)
      .select((id, ix) => { return { id, value: cards[ix] } })
      .where(data => getWhereCondition(data.value))
      .toArray();
    if (method === 'takeoff' && foregroundZ !== undefined) {
      children.forEach((target, ix) => {
        if (!isMapCard(target.value)) return;
        const x = target.value.x + parent.x;
        const y = target.value.y + parent.y;
        const z = foregroundZ + ix;
        dispatch(updateCard({ id: target.id, groupParent: parent.groupParent, x, y, z }));
      })
    } else if (method === 'delete') children.forEach(target => dispatch(deleteCard({ id: target.id })))
  }
  const putForwardMapCard = (id: string) => {
    const ix = ids.indexOf(id);
    if (currentSheet.sheetKind !== 'map' || ix === -1) return;
    const card = cards[ix] as MapCard;
    const z = card.z;
    const targets = linq.from(ids)
      .select((id, ix) => { return { id, value: cards[ix] } })
      .where(data => isMapCard(data.value) && data.value.z === z + 1 &&
        data.value.isGroup === card.isGroup && data.value.groupParent === card.groupParent
      )
      .toArray();
    if (!targets[0]) return;
    dispatch(updateCard({ id, z: z + 1 }));
    dispatch(updateCard({ id: targets[0].id, z: z }));
  }
  const putForegroundMapCard = (id: string) => {
    const ix = ids.indexOf(id);
    if (currentSheet.sheetKind !== 'map' || ix === -1) return;
    const card = cards[ix] as MapCard;
    const z = card.z;
    const targets = linq.from(ids)
      .select((id, ix) => { return { id, value: cards[ix] } })
      .where(data => isMapCard(data.value) && data.value.z > z &&
        data.value.isGroup === card.isGroup && data.value.groupParent === card.groupParent
      )
      .toArray();
    if (targets.length === 0) return;
    dispatch(updateCard({ id, z: getForegroundZ(card.isGroup, card.groupParent) - 1 }));
    targets.forEach(target => isMapCard(target.value) && dispatch(updateCard({ id: target.id, z: target.value.z - 1 })))
  }
  const putBackwardMapCard = (id: string) => {
    const ix = ids.indexOf(id);
    if (currentSheet.sheetKind !== 'map' || ix === -1) return;
    const card = cards[ix] as MapCard;
    const z = card.z;
    const targets = linq.from(ids)
      .select((id, ix) => { return { id, value: cards[ix] } })
      .where(data => isMapCard(data.value) && data.value.z === z - 1 &&
        data.value.isGroup === card.isGroup && data.value.groupParent === card.groupParent
      )
      .toArray();
    if (!targets[0]) return;
    dispatch(updateCard({ id, z: z - 1 }));
    dispatch(updateCard({ id: targets[0].id, z: z }));
  }
  const putBackmostMapCard = (id: string) => {
    const ix = ids.indexOf(id);
    if (currentSheet.sheetKind !== 'map' || ix === -1) return;
    const card = cards[ix] as MapCard;
    const z = card.z;
    const targets = linq.from(ids)
      .select((id, ix) => { return { id, value: cards[ix] } })
      .where(data => isMapCard(data.value) && data.value.z < z &&
        data.value.isGroup === card.isGroup && data.value.groupParent === card.groupParent
      )
      .toArray();
    if (targets.length === 0) return;
    dispatch(updateCard({ id, z: 0 }));
    targets.forEach(target => isMapCard(target.value) && dispatch(updateCard({ id: target.id, z: target.value.z + 1 })))
  }
  const toggleIsGroupAndSave = (id: string, card: MapCard, isGroupOld?: boolean) => {
    if (!isGroupOld && card.isGroup) {
      putDownForwardCard(false, card.groupParent, card.z);
      const newZ = getForegroundZ(true, card.groupParent);
      dispatch(updateCard({ id, ...card, z: newZ }));
    } else if (isGroupOld && !card.isGroup) {
      putDownForwardCard(true, card.groupParent, card.z);
      putUpForwardCard(false, card.groupParent, 0);
      dispatch(updateCard({ id, ...card, z: 0 }));
      const newForegroundZ = getForegroundZ(false, card.groupParent) + 1;
      operateGroupChildren(id, card, 'takeoff', newForegroundZ);
    } else dispatch(updateCard({ id, ...card }));
  }
  const addArrowExe = (id: string) => {
    if (id === connectId || !connectId || !sheetId) return;
    let newArrow: ArrowDb = { ...initial.arrow, sheetId: sheetId, fromId: connectId, toId: id };
    arrows.forEach(arrow => {
      if ((arrow.fromId === id && arrow.toId === connectId) || (arrow.fromId === connectId && arrow.toId === id)) {
        newArrow.order++;
      }
    })
    dispatch(addArrow(newArrow));
  }
  const handleChangeConnectId = (id: string) => {
    if (id === connectId) setConnectId(undefined);
    else if (connectId) {
      addArrowExe(id);
      setConnectId(id);
    } else setConnectId(id);
  }
  const openCardResizeModal = (id: string) => {
    const rect = showMenu?.anchorEl.getBoundingClientRect();
    setCardResizeAction({show: true, id: id, x: rect?.left, y: rect?.top});
  }

  const cardMenuClicked: CardMenuAction = {
    edit: (id: string) => setEditAction({show: true, id: id}),
    childAdd: (id: string, selectingCardValue: CardDb) => addChildCard(id, selectingCardValue),
    resize: (id: string) => openCardResizeModal(id),
    copy: (cardHash: CardDbHashType) => setCardClipboards(cardHash),
    link: (id: string) => setAddCardLinkAction({show: true, id: id}),
    jump: (id: string) => setJumpLinkAction({show: true, id: id}),
    forward: (id: string) => putForwardMapCard(id),
    foreground: (id: string) => putForegroundMapCard(id),
    backward: (id: string) => putBackwardMapCard(id),
    backmost: (id: string) => putBackmostMapCard(id),
    delete: (id: string, sheetId?: string) => setDeleteCardAction({show: true, id: id, sheetId: sheetId})
  };
  const arrowMenuClicked: ArrowMenuAction = {
    edit: (id: string) => {
      setTimeout(() => setEditArrowAction({ show: true, id: id }), 200)
      setSelectedId(id);
    },
    link: (id: string) => setAddArrowLinkAction({ show: true, id: id }),
    jump: (id: string) => setJumpArrowLinkAction({ show: true, id: id }),
    cutIn: (id: string) => cardCutIn(id),
    delete: (id: string) => setDeleteArrowAction({ show: true, id: id })
  };

  const addClicked = () => {
    setCardAddAction({show: true, parent: currentSheet.sheetKind});
    setBlankMenuData(undefined);
    setAddCardPosition(undefined);
  }

  return (
    <>
      <WorkAreaPaper ref={screenshotRef} style={{height: workHeight}} elevation={0} square id="work-area-paper"
        onScroll={() => {setBlankMenuData(undefined); toggleArrowMenuData(undefined);}}
      >
        <WorkAreaHeader
          infoClicked={infoClicked}
          handlingStates={handlingStates}
          handlingStateSetters={handlingStateSetters}
          currentSheet={currentSheet}
          openScreenshotDialog={() => {
            setConnectId(undefined);
            setSelectingGrid(undefined);
            setBlankMenuData(undefined);
            setStoryIndex(undefined);
            setSelectedId(undefined);
            setTimeout(() => setScreenshotAction({ show: true }), 300);
          }}
        />
        <WorkAreaBody id={workAreaBodyId}
          onMouseDown={(ev) => getClickRect(ev)}
          onContextMenu={(ev) => toggleBlankMenu(ev)}
          onWheel={() => {setBlankMenuData(undefined); toggleArrowMenuData(undefined);}}
          style={{...getWorkAreaBodySize(), minHeight: workHeight - 80, minWidth: '100%', transform: 'scale(' + getScale() + ')'}}
        >
          { currentSheet.sheetKind === 'story' &&
            <StorySheet ids={ids} cards={cards as StoryCard[]} cardMenuClicked={cardMenuClicked}
              showMenu={showMenu} setShowMenu={setShowMenu}
              storyIndex={storyIndex} setStoryIndex={setStoryIndex}
              scaleCoefficient={typeof scale === 'number' ? scale / 100 : 1} />
          }
          { currentSheet.sheetKind === 'map' &&
            <MapSheet ids={ids} cards={cards as MapCard[]} arrowIds={arrowIds} arrows={arrows}
              showMenu={showMenu} setShowMenu={setShowMenu}
              toggleArrowMenuData={toggleArrowMenuData}
              selectedId={selectedId}
              mode={mode} connectId={connectId}
              handleChangeConnectId={handleChangeConnectId}
              cardMenuClicked={cardMenuClicked}
              arrowMenuClicked={arrowMenuClicked}
              scaleCoefficient={typeof scale === 'number' ? scale / 100 : 1}
              cardResizeAction={cardResizeAction}
              putDownForwardCard={putDownForwardCard}
              getForegroundZ={getForegroundZ}
            />
          }
          { currentSheet.sheetKind === 'grid' &&
            <GridSheet ids={ids} cards={cards as GridCard[]} arrowIds={arrowIds} arrows={arrows}
              showMenu={showMenu} setShowMenu={setShowMenu}
              toggleArrowMenuData={toggleArrowMenuData}
              selectedId={selectedId}
              clickRect={clickRect} selectingGrid={selectingGrid}
              setSelectingGrid={setSelectingGrid}
              cardMenuClicked={cardMenuClicked}
              arrowMenuClicked={arrowMenuClicked}
              blankAddClicked={() => setCardAddAction({show: true, parent: currentSheet.sheetKind})}
              mode={mode} connectId={connectId}
              handleChangeConnectId={handleChangeConnectId}
              scaleCoefficient={typeof scale === 'number' ? scale / 100 : 1} />
          }
        </WorkAreaBody>
        <CardMenu showMenu={showMenu}
          onClose={() => setShowMenu(undefined)}
          ids={ids} cards={cards}
          cardMenuClicked={cardMenuClicked}
          toggleIsGroupAndSave={toggleIsGroupAndSave}
          sheetKind={currentSheet.sheetKind}
        />
        <BlankMenu onClose={() => setBlankMenuData(undefined)}
          data={blankMenuData} sheetKind={currentSheet.sheetKind}
          addCard={() => setCardAddAction({show: true, parent: currentSheet.sheetKind})}
          pasteCard={pasteCard}
        />
        <ArrowMenu onClose={() => setArrowMenuData(undefined)} data={arrowMenuData}
          removeSelect={() => setSelectedId(undefined)}
          arrowMenuClicked={arrowMenuClicked}
          closeBlankMenu={() => setBlankMenuData(undefined)}
          nodeClicked={nodeClicked}
          sheetKind={currentSheet.sheetKind}
        />
      </WorkAreaPaper>
      <AddFab variant="extended"
        onClick={() => addClicked()} color="secondary" aria-label="add" disabled={currentSheet.sheetKind === 'grid' && selectingGrid === undefined}
        style={{ right: addFabOffset }}
      >
        <AddIcon />カードを置く
      </AddFab>
      <WorkAreaBottomBar sheetId={sheetId}
        scale={scale} setScale={setScale} storyLineLength={storyLineLength}
      />
      <AddCardDialog open={cardAddAction.show}
        onClose={() => {
          setCardAddAction({show: false, parent: currentSheet.sheetKind});
          setAddCardPosition(undefined);
        }}
        parent={cardAddAction.parent}
        selectingCardValue={cardAddAction.selectingCardValue}
        ids={ids} cards={cards}
        index={storyIndex} coordinate={addCardPosition} grid={selectingGrid}
        clearSelectingGrid={() => setSelectingGrid(undefined)}
        cutInMode={cardAddAction.cutInMode}
        arrowId={cardAddAction.arrowId}
      />
      <EditDialog open={editAction.show} id={editAction.id ? editAction.id : 'no id'} onClose={() => setEditAction({show: false})} toggleIsGroupAndSave={toggleIsGroupAndSave} />
      <DeleteCardDialog open={deleteCardAction.show} onClose={() => setDeleteCardAction({show: false})} id={deleteCardAction.id} sheetId={deleteCardAction.sheetId} ids={ids} cards={cards} index={storyIndex} setStoryIndex={setStoryIndex} putDownForwardCard={putDownForwardCard} operateGroupChildren={operateGroupChildren} />
      <AddCardLinkDialog open={addCardLinkAction.show} onClose={() => setAddCardLinkAction({show: false})} id={addCardLinkAction.id} />
      <JumpLinkDialog open={jumpLinkAction.show} onClose={() => setJumpLinkAction({show: false})} id={jumpLinkAction.id} nodeClicked={nodeClicked} />
      <EditArrowDialog open={editArrowAction.show} id={editArrowAction.id} onClose={() => setEditArrowAction({show: false})} cutIn={arrowMenuClicked.cutIn} />
      <AddArrowLinkDialog open={addArrowLinkAction.show} id={addArrowLinkAction.id} onClose={() => setAddArrowLinkAction({show: false})} />
      <JumpArrowLinkDialog open={jumpArrowLinkAction.show} onClose={() => setJumpArrowLinkAction({show: false})} onEditArrowClose={() => setEditArrowAction({show: false})} id={jumpArrowLinkAction.id} nodeClicked={nodeClicked} />
      <DeleteArrowDialog open={deleteArrowAction.show} onClose={() => setDeleteArrowAction({show: false})} id={deleteArrowAction.id} />
      <DownloadScreenshotDialog open={screenshotAction.show} onClose={(() => setScreenshotAction({ show: false }))} target={screenshotRef.current} ids={ids} cards={cards} />
      { cardResizeAction.id && <CardResizeModal open={cardResizeAction.show} onClose={() => setCardResizeAction({show: false})} id={cardResizeAction.id} modalX={cardResizeAction.x} modalY={cardResizeAction.y} scale={typeof scale === 'number' ? scale / 100 : 1} /> }
    </>
  );
}

export default WorkArea;
